/**
 * Builds a structured company market insight from hiring trend data.
 * All values are raw job counts (not percentages).
 */

export interface CompanyLocationEntry {
  name: string;
  count: number;
}

export interface CompanyInsight {
  companyId: string;
  totalPositions: number;
  topLocations: CompanyLocationEntry[];
  englishPercent: number | null;
}

interface HiringTrends {
  locationDistribution: Record<string, number>;
  languageRequirements: Record<string, number>;
}

const MIN_POSITIONS_TO_SHOW = 2;
const TOP_LOCATION_COUNT = 3;
const ENGLISH_LANGUAGE_KEY = 'English';

function computeTotalPositions(locationDistribution: Record<string, number>): number {
  return Object.values(locationDistribution).reduce((sum, count) => sum + count, 0);
}

function computeTopLocations(
  locationDistribution: Record<string, number>,
  limit: number,
): CompanyLocationEntry[] {
  return Object.entries(locationDistribution)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([name, count]) => ({ name, count }));
}

function computeEnglishPercent(languageRequirements: Record<string, number>): number | null {
  const total = Object.values(languageRequirements).reduce((sum, count) => sum + count, 0);
  if (total === 0) return null;
  const englishCount = languageRequirements[ENGLISH_LANGUAGE_KEY] ?? 0;
  return Math.round((englishCount / total) * 100);
}

export function buildCompanyInsight(
  companyId: string | undefined,
  trends: HiringTrends | undefined,
): CompanyInsight | null {
  if (!companyId || !trends) return null;

  const { locationDistribution, languageRequirements } = trends;
  if (!locationDistribution || Object.keys(locationDistribution).length === 0) return null;

  const totalPositions = computeTotalPositions(locationDistribution);
  if (totalPositions < MIN_POSITIONS_TO_SHOW) return null;

  const topLocations = computeTopLocations(locationDistribution, TOP_LOCATION_COUNT);
  const englishPercent = languageRequirements
    ? computeEnglishPercent(languageRequirements)
    : null;

  return { companyId, totalPositions, topLocations, englishPercent };
}
